"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { DIFFICULTY_LABELS, DOMAIN_COLORS, DOMAIN_EMOJIS, MAIN_DECK_SIZE } from "@/lib/game/config";
import type {
  Card,
  BotDifficulty,
  GameState,
  LogEntry,
  UnitInstance,
  Side,
} from "@/lib/game/types";
import {
  initializeGame,
  playCard,
  playChampion,
  moveUnit,
  resolveShowdown,
  checkWinner,
  canAfford,
  getEnergyCost,
  getPowerCost,
  getMight,
  getEffectiveMight,
  getUnitHealth,
  getAvailableEnergy,
  isSpellCard,
  isGearCard,
  isUnit,
  isLegend,
  isBattlefieldCard,
  isChampionUnit,
  isRuneCard,
  isSignatureCard,
  isTokenCard,
  isPlayableCard,
  sanitizeText,
  getCardDomains,
  getUnitsAtBattlefield,
  getUnitsInBase,
  shuffle,
  parseKeywords,
  parseSpellEffects,
} from "@/lib/game/engine";
import { runBotTurn } from "@/lib/game/bot";
import { savedDeckToEngine, toEngineCard, uniquePrints, type EngineDeck } from "@/lib/game/adapter";
import { listDecks, type SavedDeck } from "@/lib/local-store";
import type { CardRecord } from "@/lib/types";

/** Un deck RiftHub doit avoir au moins ce nombre de cartes jouables pour être utilisé. */
const MIN_SAVED_DECK = 20;

// ─────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────
const domainColor = (domain?: string) => {
  if (!domain) return "#666";
  const first = domain.split(";")[0];
  return DOMAIN_COLORS[first] || "#666";
};

const domainEmoji = (domain?: string) => {
  if (!domain) return "🎴";
  const first = domain.split(";")[0];
  return DOMAIN_EMOJIS[first] || "🎴";
};

const cardTypeBadge = (cardType?: string) => {
  const t = (cardType ?? "").toLowerCase();
  if (t.includes("champion")) return "👑";
  if (t.includes("spell")) return "✨";
  if (t.includes("gear")) return "⚙️";
  if (t.includes("legend")) return "🏛️";
  if (t.includes("rune")) return "💎";
  if (t.includes("battlefield")) return "🏰";
  if (t.includes("token")) return "🪙";
  return "🛡️";
};

// ─────────────────────────────────────────────────────────
// MAIN PLAY PAGE
// ─────────────────────────────────────────────────────────
export default function GamePage() {
  const [records, setRecords] = useState<CardRecord[]>([]);
  const [allCards, setAllCards] = useState<Card[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [savedDecks, setSavedDecks] = useState<SavedDeck[]>([]);
  /** null = choix automatique (deck le plus récent) */
  const [myDeckChoice, setMyDeckId] = useState<string | null>(null);
  const [botDeckId, setBotDeckId] = useState<string>("");
  const [difficulty, setDifficulty] = useState<BotDifficulty>("normal");
  const [searching, setSearching] = useState(false);
  const [game, setGame] = useState<GameState | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [selectedUnit, setSelectedUnit] = useState<string | null>(null);
  const [hoveredCard, setHoveredCard] = useState<Card | null>(null);
  const [targetMode, setTargetMode] = useState<{
    type: "spell" | "move";
    handIndex?: number;
    unitId?: string;
  } | null>(null);
  const logRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    fetch("/api/cards")
      .then((r) => {
        if (!r.ok) throw new Error(`Erreur ${r.status}`);
        return r.json() as Promise<{ cards: CardRecord[] }>;
      })
      .then(({ cards }) => {
        if (!alive) return;
        setSavedDecks(listDecks());
        setRecords(cards);
        setAllCards(uniquePrints(cards).map(toEngineCard));
      })
      .catch((e: Error) => alive && setLoadError(`Impossible de charger les cartes (${e.message}).`));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [logs]);

  // ── Computed data ────────────────────────────────
  const legends = useMemo(() => allCards.filter((c) => isLegend(c)), [allCards]);

  const battlefields = useMemo(() => allCards.filter((c) => isBattlefieldCard(c)), [allCards]);

  // Decks RiftHub utilisables (légende connue + assez de cartes jouables)
  const playableDecks = useMemo(() => {
    if (records.length === 0) return [] as { saved: SavedDeck; engine: EngineDeck }[];
    const byId = new Map(records.map((c) => [c.id, c]));
    return savedDecks
      .map((saved) => ({ saved, engine: savedDeckToEngine(saved, byId) }))
      .filter((d): d is { saved: SavedDeck; engine: EngineDeck } => !!d.engine && d.engine.deck.length >= MIN_SAVED_DECK);
  }, [records, savedDecks]);

  // Par défaut : le deck le plus récent
  const myDeckId = myDeckChoice ?? playableDecks[0]?.saved.id ?? "";

  const deckableCards = useMemo(
    () =>
      allCards.filter(
        (c) =>
          isPlayableCard(c) &&
          !isLegend(c) &&
          !isBattlefieldCard(c) &&
          !isRuneCard(c) &&
          !isTokenCard(c)
      ),
    [allCards]
  );

  // ── Start game ───────────────────────────────────
  const startGame = useCallback(() => {
    if (deckableCards.length < 20 || legends.length < 2 || battlefields.length < 2)
      return;

    setSearching(true);
    setTimeout(() => {
      const newLogs: LogEntry[] = [];

      const mine = playableDecks.find((d) => d.saved.id === myDeckId)?.engine ?? null;
      const theirs = playableDecks.find((d) => d.saved.id === botDeckId)?.engine ?? null;

      // Légendes : celle du deck choisi, sinon aléatoire
      const pLegend = mine?.legend ?? shuffle(legends)[0];
      const bLegend = theirs?.legend ?? shuffle(legends.filter((l) => l.id !== pLegend.id))[0] ?? pLegend;

      // Champ de bataille : un de ceux du deck (au hasard), sinon aléatoire, et différents
      const pickBf = (own: Card[] | undefined, exclude?: string) => {
        const pool = (own?.length ? own : battlefields).filter((b) => b.id !== exclude);
        return shuffle(pool.length ? pool : battlefields)[0];
      };
      const pBF = pickBf(mine?.battlefields);
      const bBF = pickBf(theirs?.battlefields, pBF.id);

      const pDomains = getCardDomains(pLegend);
      const bDomains = getCardDomains(bLegend);

      // Deck aléatoire dans les domaines (max 3 exemplaires, champion inclus si possible)
      const buildDeck = (domains: string[]) => {
        const matching = deckableCards.filter((c) => {
          const cd = getCardDomains(c);
          return cd.length === 0 || cd.some((d) => domains.includes(d)) || c.domain === "None";
        });
        const pool = shuffle(matching);
        const champion = pool.find((c) => isChampionUnit(c));
        const deck: Card[] = champion ? [champion] : [];
        for (const c of pool) {
          if (deck.length >= MAIN_DECK_SIZE) break;
          const copies = deck.filter((d) => d.id === c.id).length;
          const n = Math.min(3 - copies, MAIN_DECK_SIZE - deck.length, 1 + Math.floor(Math.random() * 3));
          for (let i = 0; i < n; i++) deck.push(c);
        }
        return deck;
      };

      const playerDeck = mine?.deck ?? buildDeck(pDomains);
      const botDeck = theirs?.deck ?? buildDeck(bDomains);

      const state = initializeGame(
        allCards,
        pLegend,
        playerDeck,
        pBF,
        bLegend,
        botDeck,
        bBF,
        newLogs
      );

      setGame(state);
      setLogs(newLogs);
      setSearching(false);
    }, 800);
  }, [allCards, deckableCards, legends, battlefields, playableDecks, myDeckId, botDeckId]);

  // ── Handle play card from hand ───────────────────
  const handlePlayCard = useCallback(
    (handIndex: number) => {
      if (!game || game.currentTurn !== "player" || game.winner) return;

      const card = game.player.hand[handIndex];
      if (!canAfford(game.player, card)) return;

      const newLogs = [...logs];
      const next = structuredClone(game) as GameState;

      // For targeted spells, check if we need target selection
      const effects = parseSpellEffects(card);
      const needsTarget = effects.some(
        (e) => e.kind === "damage" || e.kind === "kill" || e.kind === "stun"
      );

      // Ciblage manuel seulement s'il y a au moins une cible ennemie
      if (isSpellCard(card) && needsTarget && game.opponent.units.length > 0) {
        // Enter target mode
        setTargetMode({ type: "spell", handIndex });
        return;
      }

      if (playCard(next, "player", handIndex, newLogs)) {
        next.winner = checkWinner(next);
        setGame(next);
        setLogs(newLogs);
      }
    },
    [game, logs]
  );

  // ── Handle target selection ──────────────────────
  const handleSelectTarget = useCallback(
    (targetUnitId: string) => {
      if (!game || !targetMode || game.currentTurn !== "player" || game.winner) return;

      const newLogs = [...logs];
      const next = structuredClone(game) as GameState;

      if (targetMode.type === "spell" && targetMode.handIndex !== undefined) {
        if (playCard(next, "player", targetMode.handIndex, newLogs, targetUnitId)) {
          next.winner = checkWinner(next);
          setGame(next);
          setLogs(newLogs);
        }
      } else if (targetMode.type === "move" && targetMode.unitId) {
        // handled by moveUnitTo
      }

      setTargetMode(null);
    },
    [game, logs, targetMode]
  );

  // ── Handle move unit to battlefield ──────────────
  const handleMoveUnit = useCallback(
    (unitId: string, destination: "base" | "battlefield1" | "battlefield2") => {
      if (!game || game.currentTurn !== "player" || game.winner) return;

      const newLogs = [...logs];
      const next = structuredClone(game) as GameState;

      if (moveUnit(next, "player", unitId, destination, newLogs)) {
        setGame(next);
        setLogs(newLogs);
      }
    },
    [game, logs]
  );

  // ── Handle showdown ──────────────────────────────
  const handleAttack = useCallback(
    (bfId: "battlefield1" | "battlefield2") => {
      if (!game || game.currentTurn !== "player" || game.winner) return;

      const myUnits = getUnitsAtBattlefield(game, "player", bfId);
      if (myUnits.length === 0) return;

      const newLogs = [...logs];
      const next = structuredClone(game) as GameState;

      resolveShowdown(next, "player", bfId, newLogs);
      next.winner = checkWinner(next);
      setGame(next);
      setLogs(newLogs);
    },
    [game, logs]
  );

  // ── Play champion ────────────────────────────────
  const handlePlayChampion = useCallback(() => {
    if (!game || game.currentTurn !== "player" || game.winner) return;
    if (!game.player.championAvailable || !game.player.championCard) return;
    if (!canAfford(game.player, game.player.championCard)) return;

    const newLogs = [...logs];
    const next = structuredClone(game) as GameState;

    if (playChampion(next, "player", newLogs)) {
      setGame(next);
      setLogs(newLogs);
    }
  }, [game, logs]);

  // ── End turn ─────────────────────────────────────
  const handleEndTurn = useCallback(() => {
    if (!game || game.currentTurn !== "player" || game.winner) return;

    const newLogs = [...logs];
    const next = structuredClone(game) as GameState;

    newLogs.push({
      turn: next.turnNumber,
      side: "player",
      message: "⏭️ Fin de votre tour",
      type: "info",
    });

    // Run bot turn
    runBotTurn(next, difficulty, newLogs);

    setGame(next);
    setLogs(newLogs);
  }, [game, logs, difficulty]);

  // ── Cancel target mode ───────────────────────────
  const cancelTarget = useCallback(() => setTargetMode(null), []);

  // ─────────────────────────────────────────────────
  // RENDER: Setup screen
  // ─────────────────────────────────────────────────
  if (!game) {
    return (
      <div className="px-4 py-10 space-y-8 max-w-4xl mx-auto">
        <div className="text-center space-y-6">
          <h2 className="text-5xl font-black bg-gradient-to-r from-fury via-chaos to-order bg-clip-text text-transparent">
            ⚔️ Rift Arena
          </h2>
          <p className="text-neutral-300 max-w-2xl mx-auto leading-relaxed">
            Affrontez un bot avec les <strong>vraies règles</strong> de Riftbound TCG.
            <br />
            Choisissez une Légende, conquérez les champs de bataille et soyez le
            premier à atteindre 8 points!
          </p>

          <div className="bg-neutral-900/80 border border-neutral-700 rounded-2xl p-6 space-y-4 text-left max-w-xl mx-auto">
            <h3 className="font-bold text-order text-lg">📜 Règles</h3>
            <ul className="text-sm text-neutral-300 space-y-2">
              <li>
                🏰 <strong>Objectif:</strong> Premier à <strong>8 points</strong>
              </li>
              <li>
                ⚡ <strong>Runes:</strong> Canalisez 2 runes/tour pour payer vos
                cartes
              </li>
              <li>
                🛡️ <strong>Unités:</strong> Déployez en base puis déplacez vers un
                champ de bataille
              </li>
              <li>
                ⚔️ <strong>Showdown:</strong> Le Might total s&apos;affronte, les
                morts vont à la poubelle
              </li>
              <li>
                🏆 <strong>Score:</strong> +1 point en conquérant, +1 point en
                tenant un tour
              </li>
              <li>
                ✨ <strong>Keywords:</strong> SHIELD, ASSAULT, TANK, GANKING,
                ACCELERATE...
              </li>
            </ul>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 max-w-xl mx-auto text-left">
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="text-neutral-400">Mon deck</span>
              <select
                value={myDeckId}
                onChange={(e) => setMyDeckId(e.target.value)}
                className="px-3 py-2 rounded-lg bg-neutral-900 border border-neutral-700 text-white"
              >
                <option value="">🎲 Deck aléatoire</option>
                {playableDecks.map(({ saved, engine }) => (
                  <option key={saved.id} value={saved.id}>
                    {saved.name} · {engine.legend.name} ({engine.deck.length})
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="text-neutral-400">Deck du bot</span>
              <select
                value={botDeckId}
                onChange={(e) => setBotDeckId(e.target.value)}
                className="px-3 py-2 rounded-lg bg-neutral-900 border border-neutral-700 text-white"
              >
                <option value="">🎲 Deck aléatoire</option>
                {playableDecks.map(({ saved, engine }) => (
                  <option key={saved.id} value={saved.id}>
                    {saved.name} · {engine.legend.name}
                  </option>
                ))}
              </select>
            </label>
            {playableDecks.length === 0 && (
              <p className="sm:col-span-2 text-xs text-neutral-500">
                Aucun deck RiftHub jouable ({MIN_SAVED_DECK} cartes min. + une Légende).{" "}
                <Link href="/decks" className="text-cyan-400 underline">
                  Crée ou importe un deck
                </Link>{" "}
                pour jouer avec.
              </p>
            )}
          </div>

          <div className="flex items-center justify-center gap-3">
            <span className="text-sm text-neutral-400">Difficulté</span>
            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value as BotDifficulty)}
              className="px-4 py-2 rounded-lg bg-neutral-900 border border-neutral-700 text-white"
            >
              {Object.entries(DIFFICULTY_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={startGame}
            disabled={searching || allCards.length < 20}
            title={allCards.length < 20 ? "Chargement des cartes…" : undefined}
            className="px-12 py-5 rounded-xl bg-gradient-to-r from-chaos to-fury text-white font-black text-xl disabled:opacity-50 hover:scale-105 transition-transform shadow-2xl shadow-chaos/30"
          >
            {searching ? "⏳ Recherche d'adversaire..." : allCards.length < 20 ? "⏳ Chargement des cartes…" : "⚔️ Lancer une partie"}
          </button>
          {loadError && <p className="text-sm text-red-400">{loadError}</p>}
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────
  // RENDER: Game board
  // ─────────────────────────────────────────────────
  const isMyTurn = game.currentTurn === "player" && !game.winner;
  const winnerLabel =
    game.winner === "player"
      ? "🏆 VICTOIRE!"
      : game.winner === "opponent"
      ? "💀 DÉFAITE"
      : null;

  return (
    <div className="space-y-3 py-4 px-3 sm:px-6 max-w-7xl mx-auto">
      {/* ── Header bar ────────────────────────────── */}
      <div className="bg-neutral-900/80 border border-neutral-700 rounded-xl p-3 flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm">
          <div className="font-bold text-white">
            Tour {game.turnNumber} •{" "}
            <span className={isMyTurn ? "text-order" : "text-chaos"}>
              {isMyTurn ? "Votre action" : "Tour du Bot"}
            </span>
          </div>
          <div className="text-neutral-500 text-xs">
            Premier à {game.victoryScore} points
          </div>
        </div>
        <div className="flex gap-6 text-sm">
          <ScoreBadge
            label="Vous"
            score={game.player.score}
            energy={getAvailableEnergy(game.player)}
            totalRunes={game.player.runesInPlay.length}
            color="text-order"
          />
          <ScoreBadge
            label="Bot"
            score={game.opponent.score}
            energy={getAvailableEnergy(game.opponent)}
            totalRunes={game.opponent.runesInPlay.length}
            color="text-chaos"
          />
        </div>
      </div>

      {/* ── Winner banner ─────────────────────────── */}
      {winnerLabel && (
        <div
          className={`border rounded-xl p-4 flex items-center justify-between ${
            game.winner === "player"
              ? "bg-gradient-to-r from-order/30 to-calm/30 border-order/40"
              : "bg-gradient-to-r from-chaos/30 to-fury/30 border-chaos/40"
          }`}
        >
          <div className="font-black text-2xl">{winnerLabel}</div>
          <button
            onClick={() => {
              setGame(null);
              setLogs([]);
              setTargetMode(null);
            }}
            className="px-6 py-2 rounded-lg bg-neutral-900 border border-neutral-600 font-bold hover:bg-neutral-800"
          >
            🔄 Rejouer
          </button>
        </div>
      )}

      {!winnerLabel && (
        <div className="flex justify-end -mt-1">
          <button
            onClick={() => {
              if (window.confirm("Abandonner la partie en cours ?")) {
                setGame(null);
                setLogs([]);
                setTargetMode(null);
              }
            }}
            className="text-[11px] text-neutral-500 hover:text-red-400 transition"
          >
            🏳️ Abandonner
          </button>
        </div>
      )}

      {/* ── Target mode banner ────────────────────── */}
      {targetMode && (
        <div className="bg-chaos/20 border border-chaos/40 rounded-xl p-3 flex items-center justify-between">
          <span className="text-sm font-bold text-chaos">
            🎯 Sélectionnez une cible (cliquez sur une unité ennemie, en base ou sur un champ de bataille)
          </span>
          <button
            onClick={cancelTarget}
            className="px-4 py-1 rounded-lg bg-neutral-900 text-sm"
          >
            ✕ Annuler
          </button>
        </div>
      )}

      {/* ── Legends ───────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3">
        <LegendPanel
          legend={game.player.legend}
          champion={game.player.championCard}
          championAvailable={game.player.championAvailable}
          side="player"
          onPlayChampion={isMyTurn ? handlePlayChampion : undefined}
          canAffordChampion={
            game.player.championAvailable && game.player.championCard
              ? canAfford(game.player, game.player.championCard)
              : false
          }
        />
        <LegendPanel
          legend={game.opponent.legend}
          champion={game.opponent.championCard}
          championAvailable={game.opponent.championAvailable}
          side="opponent"
        />
      </div>

      {/* ── Battlefields ──────────────────────────── */}
      <div className="grid grid-cols-2 gap-3">
        {game.battlefields.map((bf) => (
          <BattlefieldPanel
            key={bf.id}
            bf={bf}
            playerUnits={getUnitsAtBattlefield(game, "player", bf.id)}
            opponentUnits={getUnitsAtBattlefield(game, "opponent", bf.id)}
            isMyTurn={isMyTurn}
            onAttack={() => handleAttack(bf.id)}
            onSelectTarget={targetMode ? handleSelectTarget : undefined}
            selectedUnit={selectedUnit}
            victoryScore={game.victoryScore}
          />
        ))}
      </div>

      {/* ── Base zones ────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3">
        <BasePanel
          title="Votre Base"
          units={getUnitsInBase(game, "player")}
          side="player"
          battlefields={game.battlefields}
          isMyTurn={isMyTurn}
          onMoveUnit={handleMoveUnit}
        />
        <BasePanel
          title="Base Adverse"
          units={getUnitsInBase(game, "opponent")}
          side="opponent"
          battlefields={game.battlefields}
          isMyTurn={false}
          onSelectTarget={targetMode ? handleSelectTarget : undefined}
        />
      </div>

      {/* ── Hand + Controls ───────────────────────── */}
      <div className="bg-neutral-900/80 border border-neutral-700 rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-order">
            🃏 Votre Main ({game.player.hand.length})
          </h3>
          <div className="flex gap-2">
            <span className="text-xs text-neutral-400 bg-neutral-800 px-3 py-1 rounded-full">
              ⚡ {getAvailableEnergy(game.player)}/{game.player.runesInPlay.length}{" "}
              Energy
            </span>
            <span className="text-xs text-neutral-400 bg-neutral-800 px-3 py-1 rounded-full">
              📚 Deck: {game.player.mainDeck.length}
            </span>
            <span className="text-xs text-neutral-400 bg-neutral-800 px-3 py-1 rounded-full">
              🗑️ Trash: {game.player.trash.length}
            </span>
            <button
              onClick={handleEndTurn}
              disabled={!isMyTurn}
              className="px-5 py-1.5 rounded-lg bg-gradient-to-r from-order to-calm text-white text-sm font-bold disabled:opacity-40 hover:scale-105 transition-transform"
            >
              ⏭️ Fin du tour
            </button>
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-none">
          {game.player.hand.map((card, i) => {
            const affordable = isMyTurn && canAfford(game.player, card);
            return (
              <HandCard
                key={`${card.id}-${i}`}
                card={card}
                playable={affordable}
                onPlay={() => handlePlayCard(i)}
                onHover={setHoveredCard}
              />
            );
          })}
          {game.player.hand.length === 0 && (
            <div className="text-neutral-600 text-sm italic py-4">
              Main vide
            </div>
          )}
        </div>

        {/* ── Hovered card detail ──────────────────── */}
        {hoveredCard && (
          <div className="bg-neutral-800/80 border border-neutral-700 rounded-lg p-3 text-xs space-y-1">
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm">{hoveredCard.name}</span>
              <span
                className="px-2 py-0.5 rounded text-[10px] font-bold"
                style={{
                  backgroundColor: `${domainColor(hoveredCard.domain)}33`,
                  color: domainColor(hoveredCard.domain),
                }}
              >
                {hoveredCard.domain}
              </span>
              <span className="text-neutral-500">{hoveredCard.cardType}</span>
            </div>
            <div className="text-neutral-300">
              {sanitizeText(hoveredCard.description) || "Aucune description"}
            </div>
            <div className="flex gap-3 text-neutral-400">
              <span>⚡ Énergie: {getEnergyCost(hoveredCard)}</span>
              <span>💎 Pouvoir: {getPowerCost(hoveredCard)}</span>
              {getMight(hoveredCard) > 0 && (
                <span>⚔️ Might: {getMight(hoveredCard)}</span>
              )}
              {parseKeywords(hoveredCard).length > 0 && (
                <span>
                  🏷️{" "}
                  {parseKeywords(hoveredCard)
                    .map((k) => `${k.keyword}${k.value > 1 ? ` ${k.value}` : ""}`)
                    .join(", ")}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Runes ─────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3">
        <RunePanel
          title="Vos Runes"
          runes={game.player.runesInPlay}
          deckSize={game.player.runeDeck.length}
        />
        <RunePanel
          title="Runes Bot"
          runes={game.opponent.runesInPlay}
          deckSize={game.opponent.runeDeck.length}
        />
      </div>

      {/* ── Log ───────────────────────────────────── */}
      <div className="bg-neutral-900/80 border border-neutral-700 rounded-xl p-4">
        <h3 className="font-bold text-neutral-300 mb-2 text-sm">
          📜 Journal de combat
        </h3>
        <div
          ref={logRef}
          className="max-h-48 overflow-y-auto space-y-0.5 text-xs font-mono scrollbar-none"
        >
          {logs.map((entry, i) => (
            <div
              key={i}
              className={`py-0.5 ${
                entry.type === "score"
                  ? "text-yellow-400 font-bold"
                  : entry.type === "combat"
                  ? "text-red-400"
                  : entry.type === "death"
                  ? "text-red-500"
                  : entry.type === "spell"
                  ? "text-purple-400"
                  : entry.type === "system"
                  ? "text-blue-400"
                  : "text-neutral-400"
              }`}
            >
              {entry.message}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────

function ScoreBadge({
  label,
  score,
  energy,
  totalRunes,
  color,
}: {
  label: string;
  score: number;
  energy: number;
  totalRunes: number;
  color: string;
}) {
  return (
    <div className="text-right text-xs">
      <div className={`font-bold ${color}`}>{label}</div>
      <div className="text-neutral-300">
        🏆 {score} pts • ⚡ {energy}/{totalRunes}
      </div>
    </div>
  );
}

function LegendPanel({
  legend,
  champion,
  championAvailable,
  side,
  onPlayChampion,
  canAffordChampion,
}: {
  legend: Card | null;
  champion: Card | null;
  championAvailable: boolean;
  side: Side;
  onPlayChampion?: () => void;
  canAffordChampion?: boolean;
}) {
  if (!legend) return null;
  const isPlayer = side === "player";

  return (
    <div
      className={`bg-neutral-900/80 border rounded-xl p-3 ${
        isPlayer ? "border-order/30" : "border-chaos/30"
      }`}
    >
      <div className="flex items-center gap-3">
        {legend.images?.small && (
          <img
            src={legend.images.small}
            alt={legend.name}
            className="w-12 h-12 rounded-lg object-cover"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm truncate">
            🏛️ {legend.name}
          </div>
          <div className="text-[10px] text-neutral-400 truncate">
            {sanitizeText(legend.description)}
          </div>
          <div className="flex gap-1 mt-1">
            {getCardDomains(legend).map((d) => (
              <span
                key={d}
                className="px-1.5 py-0.5 rounded text-[9px] font-bold"
                style={{
                  backgroundColor: `${DOMAIN_COLORS[d]}33`,
                  color: DOMAIN_COLORS[d],
                }}
              >
                {DOMAIN_EMOJIS[d]} {d}
              </span>
            ))}
          </div>
        </div>
        {champion && (
          <div className="text-center">
            {champion.images?.small && (
              <img
                src={champion.images.small}
                alt={champion.name}
                className="w-10 h-10 rounded-lg object-cover mx-auto"
              />
            )}
            <div className="text-[9px] text-neutral-400 mt-0.5 truncate max-w-[60px]">
              {champion.name}
            </div>
            {isPlayer && championAvailable && onPlayChampion && (
              <button
                onClick={onPlayChampion}
                disabled={!canAffordChampion}
                className="mt-1 px-2 py-0.5 rounded text-[9px] bg-order/30 text-order font-bold disabled:opacity-40 hover:bg-order/50 transition"
              >
                👑 Jouer ({getEnergyCost(champion)}⚡)
              </button>
            )}
            {!championAvailable && (
              <span className="text-[9px] text-neutral-600">En jeu</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function BattlefieldPanel({
  bf,
  playerUnits,
  opponentUnits,
  isMyTurn,
  onAttack,
  onSelectTarget,
  selectedUnit,
  victoryScore,
}: {
  bf: import("@/lib/game/types").BattlefieldState;
  playerUnits: UnitInstance[];
  opponentUnits: UnitInstance[];
  isMyTurn: boolean;
  onAttack: () => void;
  onSelectTarget?: (id: string) => void;
  selectedUnit: string | null;
  victoryScore: number;
}) {
  const controlColor =
    bf.controlledBy === "player"
      ? "border-order/50 bg-order/5"
      : bf.controlledBy === "opponent"
      ? "border-chaos/50 bg-chaos/5"
      : "border-neutral-700 bg-neutral-900/60";

  const readyAttackers = playerUnits.filter((u) => !u.stunned).length;
  const canAttack =
    isMyTurn && readyAttackers > 0 && (opponentUnits.length > 0 || bf.controlledBy !== "player");

  return (
    <div className={`border rounded-xl p-3 space-y-2 ${controlColor}`}>
      {/* BF header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="font-bold text-sm flex items-center gap-1">
            🏰 {bf.card.name}
            {bf.controlledBy && (
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded ${
                  bf.controlledBy === "player"
                    ? "bg-order/30 text-order"
                    : "bg-chaos/30 text-chaos"
                }`}
              >
                {bf.controlledBy === "player" ? "Vôtre" : "Adverse"}
              </span>
            )}
          </div>
          <div className="text-[10px] text-neutral-400">
            {sanitizeText(bf.card.description)}
          </div>
        </div>
        {canAttack && (
          <button
            onClick={onAttack}
            className="px-3 py-1.5 rounded-lg bg-fury/30 text-fury text-xs font-bold hover:bg-fury/50 transition animate-pulse"
          >
            ⚔️ Showdown!
          </button>
        )}
      </div>

      {/* Units here */}
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <div className="text-[10px] text-order font-bold">
            Vous ({playerUnits.length})
          </div>
          <div className="space-y-1 min-h-[40px]">
            {playerUnits.map((u) => (
              <UnitCard key={u.instanceId} unit={u} side="player" compact />
            ))}
          </div>
        </div>
        <div className="space-y-1">
          <div className="text-[10px] text-chaos font-bold">
            Bot ({opponentUnits.length})
          </div>
          <div className="space-y-1 min-h-[40px]">
            {opponentUnits.map((u) => (
              <UnitCard
                key={u.instanceId}
                unit={u}
                side="opponent"
                compact
                onClick={onSelectTarget ? () => onSelectTarget(u.instanceId) : undefined}
                highlighted={!!onSelectTarget}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function BasePanel({
  title,
  units,
  side,
  battlefields,
  isMyTurn,
  onMoveUnit,
  onSelectTarget,
}: {
  title: string;
  units: UnitInstance[];
  side: Side;
  battlefields: [import("@/lib/game/types").BattlefieldState, import("@/lib/game/types").BattlefieldState];
  isMyTurn: boolean;
  onMoveUnit?: (
    unitId: string,
    dest: "base" | "battlefield1" | "battlefield2"
  ) => void;
  onSelectTarget?: (id: string) => void;
}) {
  const isPlayer = side === "player";
  const borderColor = isPlayer ? "border-order/20" : "border-chaos/20";

  return (
    <div
      className={`bg-neutral-900/60 border ${borderColor} rounded-xl p-3 space-y-2`}
    >
      <div className="text-xs font-bold text-neutral-300">
        {isPlayer ? "🏠" : "🤖"} {title} ({units.length})
      </div>
      <div className="flex flex-wrap gap-2 min-h-[50px]">
        {units.map((u) => (
          <div key={u.instanceId} className="space-y-1">
            <UnitCard
              unit={u}
              side={side}
              compact
              onClick={onSelectTarget ? () => onSelectTarget(u.instanceId) : undefined}
              highlighted={!!onSelectTarget}
            />
            {isPlayer && isMyTurn && !u.exhausted && onMoveUnit && (
              <div className="flex gap-1">
                {battlefields.map((bf) => (
                  <button
                    key={bf.id}
                    onClick={() => onMoveUnit(u.instanceId, bf.id)}
                    className="px-1.5 py-0.5 rounded text-[8px] bg-order/20 text-order hover:bg-order/40 transition"
                    title={`Déplacer vers ${bf.card.name}`}
                  >
                    ➡️ {bf.card.name.slice(0, 8)}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
        {units.length === 0 && (
          <div className="text-neutral-600 text-[10px] italic py-2">Vide</div>
        )}
      </div>
    </div>
  );
}

function UnitCard({
  unit,
  side,
  compact,
  onClick,
  highlighted,
}: {
  unit: UnitInstance;
  side: Side;
  compact?: boolean;
  onClick?: () => void;
  highlighted?: boolean;
}) {
  const effectiveMight = getEffectiveMight(unit);
  const maxHp = getUnitHealth(unit);
  const hp = Math.max(0, maxHp - unit.damageTaken);
  const isPlayer = side === "player";
  const borderHue = isPlayer ? "border-order/40" : "border-chaos/40";

  const keywordBadges = unit.keywords
    .filter(
      (k) =>
        !["ACTION", "REACTION", "LEGION"].includes(k.keyword)
    )
    .map(
      (k) =>
        `${k.keyword}${k.value > 1 ? ` ${k.value}` : ""}`
    );

  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className={`text-left rounded-lg p-1.5 transition text-[10px] w-full ${
        highlighted
          ? "bg-chaos/20 border-chaos border-2 cursor-crosshair animate-pulse"
          : `bg-neutral-950 border ${borderHue} ${
              onClick ? "cursor-pointer hover:border-fury/60" : ""
            }`
      } ${unit.exhausted ? "opacity-60" : ""} ${
        unit.stunned ? "grayscale" : ""
      }`}
    >
      <div className="font-bold truncate leading-tight">
        {cardTypeBadge(unit.card.cardType)} {unit.card.name}
      </div>
      <div className="flex items-center gap-1 mt-0.5 flex-wrap">
        <span
          className="px-1 py-0 rounded font-bold"
          style={{
            backgroundColor: `${domainColor(unit.card.domain)}33`,
            color: domainColor(unit.card.domain),
          }}
        >
          ⚔️ {effectiveMight}
        </span>
        <span
          className={`px-1 py-0 rounded font-bold ${
            hp < maxHp ? "bg-red-500/30 text-red-400" : "bg-green-500/20 text-green-400"
          }`}
        >
          ❤️ {hp}/{maxHp}
        </span>
        {unit.exhausted && (
          <span className="px-1 py-0 rounded bg-neutral-700 text-neutral-400">
            💤
          </span>
        )}
        {unit.stunned && (
          <span className="px-1 py-0 rounded bg-purple-500/30 text-purple-400">
            ⛓️
          </span>
        )}
        {unit.buffCount > 0 && (
          <span className="px-1 py-0 rounded bg-yellow-500/20 text-yellow-400">
            +{unit.buffCount}
          </span>
        )}
        {unit.tempMightBonus !== 0 && (
          <span
            className={`px-1 py-0 rounded ${
              unit.tempMightBonus > 0
                ? "bg-green-500/20 text-green-400"
                : "bg-red-500/20 text-red-400"
            }`}
          >
            {unit.tempMightBonus > 0 ? "+" : ""}
            {unit.tempMightBonus}
          </span>
        )}
      </div>
      {keywordBadges.length > 0 && (
        <div className="flex gap-0.5 mt-0.5 flex-wrap">
          {keywordBadges.map((kw, i) => (
            <span
              key={i}
              className="px-1 py-0 rounded bg-mind/20 text-mind text-[8px] font-bold"
            >
              {kw}
            </span>
          ))}
        </div>
      )}
    </button>
  );
}

function HandCard({
  card,
  playable,
  onPlay,
  onHover,
}: {
  card: Card;
  playable: boolean;
  onPlay: () => void;
  onHover: (c: Card | null) => void;
}) {
  return (
    <button
      onClick={playable ? onPlay : undefined}
      onMouseEnter={() => onHover(card)}
      onMouseLeave={() => onHover(null)}
      disabled={!playable}
      className={`min-w-[130px] max-w-[130px] text-left rounded-xl overflow-hidden border transition-all flex-shrink-0 ${
        playable
          ? "border-order/50 hover:border-order hover:scale-105 hover:shadow-xl hover:shadow-order/20 cursor-pointer"
          : "border-neutral-700 opacity-50"
      }`}
    >
      {card.images?.small && (
        <img
          src={card.images.small}
          alt={card.name}
          className="w-full h-28 object-cover object-top"
        />
      )}
      <div className="bg-neutral-950 p-2 space-y-1">
        <div className="text-[10px] text-neutral-500">
          {cardTypeBadge(card.cardType)} {card.cardType}
        </div>
        <div className="font-bold text-xs leading-tight truncate">
          {card.name}
        </div>
        <div className="flex gap-1 flex-wrap">
          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-calm/30 text-calm">
            ⚡ {getEnergyCost(card)}
          </span>
          {getPowerCost(card) > 0 && (
            <span
              className="px-1.5 py-0.5 rounded text-[9px] font-bold"
              style={{
                backgroundColor: `${domainColor(card.domain)}33`,
                color: domainColor(card.domain),
              }}
            >
              💎 {getPowerCost(card)}
            </span>
          )}
          {getMight(card) > 0 && (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-fury/30 text-fury">
              ⚔️ {getMight(card)}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function RunePanel({
  title,
  runes,
  deckSize,
}: {
  title: string;
  runes: import("@/lib/game/types").RuneInstance[];
  deckSize: number;
}) {
  return (
    <div className="bg-neutral-900/60 border border-neutral-700/50 rounded-xl p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-neutral-300">{title}</span>
        <span className="text-[10px] text-neutral-500">
          Deck: {deckSize} restantes
        </span>
      </div>
      <div className="flex gap-1 flex-wrap">
        {runes.map((r) => (
          <span
            key={r.instanceId}
            className={`w-7 h-7 rounded-md flex items-center justify-center text-sm font-bold transition ${
              r.exhausted ? "opacity-30 grayscale" : "shadow-md"
            }`}
            style={{
              backgroundColor: `${DOMAIN_COLORS[r.domain] || "#666"}33`,
              color: DOMAIN_COLORS[r.domain] || "#666",
              border: `1px solid ${DOMAIN_COLORS[r.domain] || "#666"}44`,
            }}
            title={`${r.domain} Rune${r.exhausted ? " (épuisée)" : ""}`}
          >
            {DOMAIN_EMOJIS[r.domain] || "💎"}
          </span>
        ))}
        {runes.length === 0 && (
          <span className="text-neutral-600 text-[10px] italic">
            Aucune rune canalisée
          </span>
        )}
      </div>
    </div>
  );
}
